/**
 * WooCommerce Store API
 * All API calls to WooCommerce Store API
 */

const STORE_API_BASE = `${window.location.origin}/wp-json/wc/store/v1`

// Theme REST namespace (garrett/v1), localized with a trailing slash
const THEME_API_BASE = window.garrett?.resturl || `${window.location.origin}/wp-json/garrett/v1/`

/**
 * Add product to cart
 * @param {number} productId
 * @param {number} quantity
 * @param {Object|null} crossSell attribution for cross-sell adds:
 *   { source: 'cross_sell_drawer'|'cart_cross_sell_carousel', origin: number|null, position: number|null }
 *   Sent as request headers so the server-side GA event and the order item
 *   meta can attribute the add (see WooCrossSellTracking).
 * @returns {Promise<Object>} Cart data
 */
export async function addToCart(productId, quantity = 1, crossSell = null) {
  const headers = {
    'Content-Type': 'application/json',
    'X-WC-Store-API-Nonce': window.garrett?.store_api_nonce
  }

  if (crossSell?.source) {
    headers['X-Garrett-CS-Source'] = crossSell.source
    if (crossSell.origin) headers['X-Garrett-CS-Origin'] = String(crossSell.origin)
    if (crossSell.position) headers['X-Garrett-CS-Position'] = String(crossSell.position)
  }

  const response = await fetch(`${STORE_API_BASE}/cart/add-item`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      id: parseInt(productId),
      quantity: parseInt(quantity)
    })
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to add product to cart')
  }

  return response.json()
}

/**
 * Get current cart
 * @returns {Promise<Object>} Cart data
 */
export async function getCart() {
  const response = await fetch(`${STORE_API_BASE}/cart`, {
    method: 'GET',
    headers: {
      'X-WC-Store-API-Nonce': window.garrett?.store_api_nonce
    }
  })

  if (!response.ok) {
    throw new Error('Failed to get cart')
  }

  return response.json()
}

/**
 * Update cart item quantity
 * @param {string} cartItemKey
 * @param {number} quantity
 * @returns {Promise<Object>} Cart data
 */
export async function updateCartItemQuantity(cartItemKey, quantity) {
  const response = await fetch(`${STORE_API_BASE}/cart/update-item`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-WC-Store-API-Nonce': window.garrett?.store_api_nonce
    },
    body: JSON.stringify({
      key: cartItemKey,
      quantity: parseInt(quantity)
    })
  })

  if (!response.ok) {
    throw new Error('Failed to update cart item')
  }

  return response.json()
}

/**
 * Remove item from cart
 * @param {string} cartItemKey
 * @returns {Promise<Object>} Cart data
 */
export async function removeCartItem(cartItemKey) {
  const response = await fetch(`${STORE_API_BASE}/cart/remove-item`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-WC-Store-API-Nonce': window.garrett?.store_api_nonce
    },
    body: JSON.stringify({
      key: cartItemKey
    })
  })

  if (!response.ok) {
    throw new Error('Failed to remove cart item')
  }

  return response.json()
}

/**
 * Remove coupon from cart
 * @param {string} couponCode
 * @returns {Promise<Object>} Cart data
 */
export async function removeCoupon(couponCode) {
  const response = await fetch(`${STORE_API_BASE}/cart/remove-coupon`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-WC-Store-API-Nonce': window.garrett?.store_api_nonce
    },
    body: JSON.stringify({
      code: couponCode
    })
  })

  if (!response.ok) {
    throw new Error('Failed to remove coupon')
  }

  return response.json()
}

/**
 * GET from the theme REST namespace with cookie auth. A stale X-WP-Nonce
 * (possible — the nonce is printed into cached HTML) would fail the whole
 * request with 403, so it degrades to the anonymous answer instead.
 */
async function themeApiGet(url) {
  let response = await fetch(url, {
    method: 'GET',
    headers: {
      'X-WP-Nonce': window.garrett?.rest_nonce
    }
  })

  if (response.status === 403) {
    response = await fetch(url, { method: 'GET' })
  }

  return response
}

/**
 * Get drawer content via the theme REST API (GET /garrett/v1/cart/drawer).
 * Same payload the old admin-ajax handler (get_drawer_content) returned —
 * only the transport changed.
 * @returns {Promise<Object>} Drawer HTML and cart icon HTML
 */
export async function getDrawerContent() {
  const response = await themeApiGet(`${THEME_API_BASE}cart/drawer`)

  if (!response.ok) {
    throw new Error('Failed to get drawer content')
  }

  return response.json()
}

/**
 * Get the quick-view markup for one product
 * (GET /garrett/v1/product/quick-view)
 * @param {number} productId
 * @param {number} position position in the recommendations list, carried
 *   through so an add from the modal is attributed like an add from the row
 * @returns {Promise<Object>} { html }
 */
export async function getProductQuickView(productId, position = 0) {
  const params = new URLSearchParams({ product_id: String(productId) })

  if (position) {
    params.set('position', String(position))
  }

  const response = await themeApiGet(`${THEME_API_BASE}product/quick-view?${params.toString()}`)

  if (!response.ok) {
    throw new Error('Failed to get product quick view')
  }

  return response.json()
}

/**
 * Get cross-sell drawer content for a just-added product
 * (GET /garrett/v1/cart/cross-sell-drawer)
 * @param {number|null} productId the product that was just added
 * @returns {Promise<Object>} { html, cart_icon_html, cart_count }
 */
export async function getCrossSellDrawerContent(productId = null) {
  const params = new URLSearchParams()
  if (productId) {
    params.set('product_id', String(productId))
  }

  const query = params.toString()
  const response = await themeApiGet(`${THEME_API_BASE}cart/cross-sell-drawer${query ? `?${query}` : ''}`)

  if (!response.ok) {
    throw new Error('Failed to get cross-sell drawer content')
  }

  return response.json()
}
